import {
  InvoiceBeneficiaryTarget,
  PartyRole,
  PartyTarget,
  UserBeneficiary,
} from '@hero/schema';

/**
 * ============================================================================
 * Auth-v2: identity-derived helpers on top of `PartyRole[]`.
 * ============================================================================
 *
 * The legacy `User.type` (`{ groups, roles }`) machinery has been removed —
 * see the auth-v2 refactor plan and `docs/relations-v2/08-user-identity.md`.
 * The registry that lived in `libs/shared/core/src/config/user.config.ts`
 * (`allUserTypes`, `mainTypes`, `memberTypesAndUserTypes`) is gone, along
 * with the helpers that operated on it (`isUser`, `isUserType`,
 * `getUserSimpleType`, `getUserMemberType`, `getUserType`,
 * `getUserTypeByMember`, `getIndividualUserSimpleType`,
 * `__deprecatedDeriveLegacyUserType`, `deriveUserRoles`, `getUserInfo`).
 *
 * All callers now branch on `User.roles[]` / `Org.roles[]` (`PartyRole[]`)
 * or on the current-user service accessors (`hasPartyRole`,
 * `hasAnyPartyRole`, `memberType`, `orgRoles`) instead.
 */

/**
 * Post relations-v2 hard cutover — `PartyTarget` is the 7-value
 * canonical enum ({@link targetSchema}). These helpers preserve their
 * legacy names so callsites don't churn, but they now branch on the
 * canonical `Target` values only.
 */
export const isBusinessCustomer = (type: PartyTarget) => {
  return type === 'org';
};

/** Any customer-side target — an `Org` (business_customer role) or a
 * standalone `User` (individual_customer) or a CRM `Client`. */
export const isAnyCustomer = (type: PartyTarget | string) => {
  return type === 'org' || type === 'user' || type === 'client';
};

/** Any carrier-side target — an `Org` acting as carrier / broker or a
 * `Driver`. */
export const isAnyCarrier = (type: PartyTarget | string) => {
  return type === 'org' || type === 'driver';
};

// ============================================================================
// PartyRole[] helpers (relations-v2 canonical)
// ============================================================================
//
// `Org.roles[]` and `Client.roles[]` are multi-valued in the network
// model — a single Org may be `business_customer` AND `carrier_company`
// AND `broker` at once. The helpers below answer the common "does
// this set of roles include any X-side role?" questions consistently.
//
// Pick the right family:
//   - `hasCustomerRole(roles)` / `hasCarrierRole(roles)` — does the
//     set contain ANY role on that side?
//   - `isCustomerOnly(roles)` / `isAnyCarrier(roles)` / etc. — exact
//     classification used for UI defaults (the "is the org purely
//     a customer?" question that drives e.g. the pricing sheet
//     sell/buy default).
//
// All helpers operate on the role array directly (no `User.type`
// detour) — wire them on the read-back doc's `roles[]`.

/** Any "customer-side" role — a party who buys transport. */
const CUSTOMER_SIDE_ROLES: ReadonlySet<PartyRole> = new Set([
  'business_customer',
  'individual_customer',
]);

/** Any "carrier-side" role — a party who sells transport / capacity. */
const CARRIER_SIDE_ROLES: ReadonlySet<PartyRole> = new Set([
  'carrier_company',
  'broker',
]);

/** `true` if `roles[]` contains any customer-side role. */
export const hasCustomerRole = (roles: readonly PartyRole[] | undefined): boolean =>
  (roles ?? []).some((r) => CUSTOMER_SIDE_ROLES.has(r));

/** `true` if `roles[]` contains any carrier-side role (carrier company or broker). */
export const hasAnyCarrierRole = (roles: readonly PartyRole[] | undefined): boolean =>
  (roles ?? []).some((r) => CARRIER_SIDE_ROLES.has(r));

/** `true` if every role is customer-side (no carrier/broker). */
export const isCustomerOnly = (roles: readonly PartyRole[] | undefined): boolean => {
  const list = roles ?? [];
  if (list.length === 0) return false;
  return list.every((r) => CUSTOMER_SIDE_ROLES.has(r));
};

/** `true` if every role is carrier-side (no customer). */
export const isCarrierSideOnly = (roles: readonly PartyRole[] | undefined): boolean => {
  const list = roles ?? [];
  if (list.length === 0) return false;
  return list.every((r) => CARRIER_SIDE_ROLES.has(r));
};

/** `true` if the only role is `carrier_company` (no broker, no customer). */
export const isCarrierCompanyOnly = (roles: readonly PartyRole[] | undefined): boolean => {
  const list = roles ?? [];
  return list.length === 1 && list[0] === 'carrier_company';
};

/** `true` if the only role is `broker` (no carrier company, no customer). */
export const isBrokerOnly = (roles: readonly PartyRole[] | undefined): boolean => {
  const list = roles ?? [];
  return list.length === 1 && list[0] === 'broker';
};

/** `true` if the only role is `business_customer`. */
export const isBusinessCustomerOnly = (
  roles: readonly PartyRole[] | undefined,
): boolean => {
  const list = roles ?? [];
  return list.length === 1 && list[0] === 'business_customer';
};

/** `true` if the only role is `individual_customer`. */
export const isIndividualCustomerOnly = (
  roles: readonly PartyRole[] | undefined,
): boolean => {
  const list = roles ?? [];
  return list.length === 1 && list[0] === 'individual_customer';
};

/**
 * Default pricing-sheet side ('sell' / 'buy') for a `(me, them)` pair.
 *
 * The pricing sheet has two scenarios: "my selling rate" (`'sell'`,
 * meaning I'm the seller, the other party buys) and "my buying rate"
 * (`'buy'`, the other party sells to me).
 *
 * Decision rule (in priority order):
 *
 *   1. **Counterparty is carrier-side** (`carrier_company` and/or
 *      `broker`) → `'buy'`. I'm buying capacity from them.
 *   2. **Counterparty is customer-side only** (`business_customer`
 *      and/or `individual_customer`) → `'sell'`. I'm selling
 *      transport to them.
 *   3. **Counterparty roles unknown / mixed** → fall back to my own
 *      role: customer-only orgs always buy (`'buy'`); carrier-only
 *      orgs always sell (`'sell'`); mixed orgs default to `'sell'`
 *      (the legacy fallback the sheet has always used).
 *
 * The counterparty's role wins when it's unambiguous because it's
 * the more specific signal — a Broker org viewing a customer client
 * still wants to see "what should I quote them?" (sell), and a
 * Carrier org viewing a broker client still wants to see "what does
 * the broker pay for capacity?" (buy).
 */
export const defaultPricingSide = (
  myRoles: readonly PartyRole[] | undefined,
  theirRoles: readonly PartyRole[] | undefined,
): 'sell' | 'buy' => {
  if (hasAnyCarrierRole(theirRoles)) return 'buy';
  if (hasCustomerRole(theirRoles)) return 'sell';
  // Ambiguous counterparty — fall back to "what kind of org am I?".
  if (isCustomerOnly(myRoles)) return 'buy';
  if (isCarrierSideOnly(myRoles)) return 'sell';
  return 'sell';
};

/**
 * Legacy broker-era invoice helper — collapses a
 * {@link UserBeneficiary}'s target into the widened
 * {@link InvoiceBeneficiaryTarget} accepted by
 * {@link invoiceBeneficiarySchema}. Historically renamed from
 * `convertBeneficiaryToRelationTarget` when `relationSchema` was
 * retired; kept for `InvoiceService.getInvoiceParties`, which is
 * itself deprecated in favor of the per-leg capture flow.
 *
 * @deprecated Removed once `InvoiceService.getInvoiceParties`
 * (broker-era Hero-in-the-middle invoice flow) is retired.
 */
export const convertBeneficiaryToRelationTarget = (
  beneficiaryTarget: UserBeneficiary['target'],
): InvoiceBeneficiaryTarget => {
  // `super_admin_system` collapses to the canonical `super_admin`;
  // everything else is already assignable to the widened union.
  if (beneficiaryTarget === 'super_admin_system') return 'super_admin';
  return beneficiaryTarget as InvoiceBeneficiaryTarget;
};
